import { money } from "../lib/format";
import { TIMEFRAME_SOURCE } from "../lib/rounds";
import { storage, type TypedStorage } from "../lib/storage";
import { simulateTrade, type Trade, type TradeResult } from "../lib/trade";
import type { Round } from "../types";
import { useAccountStore } from "./accountStore";
import type { HistoryEntryInput, HistoryOutcome } from "./historyStore";
import { createRoundLoop, migrateStorageKey } from "./roundLoop";
import { settingsRoundFilter, useSettingsStore } from "./settingsStore";

/**
 * The Trade Loop (spec §7): place a numeric Long/Short with stop-loss,
 * take-profit, and a dollar risk over the bank the timeframe setting names;
 * the reveal simulates it over the Forward (lib/trade) into realized dollar
 * P&L, applied to the Account balance. Size is the player's own risk divided
 * by the stop distance, so a stop-out always costs exactly what was risked
 * (lib/trade). Round selection honors the markets and Context length settings
 * (GRA-90) like Predict; difficulty stays Predict-only since a trade sets its
 * own stop/target. No hit-rate scoring: the Account curve is the scoreboard.
 *
 * Since ADR-0013 every bank stores a Forward path, so Trade plays all three
 * timeframes: entry, stop, target, ATR sizing and P&L are all in price terms,
 * and a "candle held" is one candle of whatever the timeframe is. The source is
 * read from the setting at `init`, and a timeframe switch re-inits the Loop
 * (the Loop id itself does not carry the timeframe, so nothing else would).
 */

const SEEN_KEY = "seen:trade";

const REASON_TEXT: Record<TradeResult["reason"], string> = {
  stop: "stopped out",
  target: "hit target",
  timeout: "closed at end",
};

/** This Loop's history entry: the placed trade against its simulated P&L. */
function historyTrade(
  round: Round,
  trade: Trade,
  reveal: TradeResult,
  _correct: boolean | undefined,
  note: string | undefined,
): HistoryEntryInput {
  // Matches accountStore's win/loss classification: >0 win, <0 loss, 0 flat.
  const outcome: HistoryOutcome = reveal.pnl > 0 ? "win" : reveal.pnl < 0 ? "loss" : "flat";
  return {
    mode: "trade",
    timeframe: useSettingsStore.getState().timeframe,
    horizon: null,
    kind: "free",
    roundId: round.id,
    // The Context slice the trade was read against, so a review charts the
    // same candles (and therefore strikes the levels off the same entry).
    contextLength: useSettingsStore.getState().contextLength,
    // The levels as numbers, next to the formatted `choice` line: a review
    // redraws them and re-simulates the trade from these (GRA-154).
    trade: {
      side: trade.direction === 1 ? "long" : "short",
      entry: trade.entry,
      stopLoss: trade.stopLoss,
      takeProfit: trade.takeProfit,
      risk: trade.risk,
    },
    instrumentId: round.instrumentId,
    instrumentName: round.instrumentName,
    dateRange: round.dateRange,
    choice: `${trade.direction === 1 ? "Long" : "Short"} · risk ${money(trade.risk)}`,
    outcome,
    detail: `${REASON_TEXT[reveal.reason]} · ${money(reveal.pnl)} · ${reveal.barsHeld} candle${
      reveal.barsHeld === 1 ? "" : "s"
    } held`,
    pnl: reveal.pnl,
    note,
  };
}

/** Build a Trade Loop store; dependencies injectable by tests. */
export function createTradeStore(
  deps: {
    storage?: TypedStorage;
    /** Realize a trade's P&L; defaults to the Account balance. */
    applyTrade?: (pnl: number) => void;
  } = {},
) {
  const applyTrade =
    deps.applyTrade ?? ((pnl: number) => useAccountStore.getState().applyTrade(pnl));
  return createRoundLoop({
    // Read at `init`, so the Loop plays whichever bank the timeframe setting
    // names right now (the subscription below re-inits it when that changes).
    openSource: (baseUrl, fetchFn) =>
      TIMEFRAME_SOURCE[useSettingsStore.getState().timeframe](baseUrl, fetchFn),
    seenKey: SEEN_KEY,
    filter: () => settingsRoundFilter(useSettingsStore.getState().timeframe),
    resolve: (round: Round, trade: Trade): { reveal: TradeResult } => {
      const result = simulateTrade(trade, round.forward);
      applyTrade(result.pnl);
      return { reveal: result };
    },
    history: historyTrade,
    storage: deps.storage,
    analytics: {
      mode: "trade",
      // Trade has a timeframe (ADR-0013) but no horizon of its own; it walks
      // the same Forward path the trend game does, so it is reported that way.
      timeframe: () => useSettingsStore.getState().timeframe,
      horizon: "trend",
      preset: () => useSettingsStore.getState().preset,
      // resolve() has no notion of "correct" (a trade is P&L, not a guess);
      // report the win/loss the trade itself realized instead.
      correct: (_round, _trade, reveal) => reveal.pnl > 0,
    },
  });
}

// Pre-rename key (GRA-96 vocabulary sweep, finished at the storage tier).
migrateStorageKey(storage, "seen:advanced", SEEN_KEY);

export const useTradeStore = createTradeStore();

// Trade's Loop id carries no timeframe (there is one Trade Loop, not three),
// so App's per-Loop `init` never fires again when the setting changes. Re-open
// the source here instead, which swaps the bank and starts a fresh Round.
useSettingsStore.subscribe((state, prevState) => {
  if (state.timeframe === prevState.timeframe) return;
  // Only once the Loop has actually opened a bank: before that, App's own
  // first `init` reads the setting as it stands and there is nothing to swap.
  if (useTradeStore.getState().index.length === 0) return;
  void useTradeStore.getState().init();
});
